// Question text is plain text with LaTeX, but text pasted from Word, Google Docs or a web page
// (and CSV cells copied from them) can carry HTML: <p>, <br>, <b>, H<sub>2</sub>O, &nbsp;.
// These helpers read that HTML safely so it shows as formatting instead of raw tags:
//  - LaTeX is set aside first, so "\( a<b \)" is never read as a tag;
//  - the HTML is parsed in an inert document (DOMParser runs no scripts and loads no images);
//  - only basic formatting tags are kept, with no attributes except safe links.
// The server cleans the same way on save (App\Support\QuestionHtml); the same file lives in both apps.

const MATH = /\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$\$[\s\S]*?\$\$|\$[^$\n]+?\$/g;

// Tags that make a value HTML (formatting to keep, or unsafe tags to drop); any other "<" is maths or prose, e.g. "x < 5"
const TAG =
  /<\/?(?:p|br|b|strong|i|em|u|s|strike|sub|sup|ul|ol|li|h[1-6]|blockquote|pre|code|span|div|a|hr|table|thead|tbody|tr|th|td|font|script|style|iframe|object|embed|img|svg|video|audio|form|input|button|link|meta)\b[^>]*>/i;
const ENTITY = /&(?:nbsp|amp|lt|gt|quot|#\d+|#x[0-9a-f]+);/i;

const ALLOWED = new Set([
  "p", "br", "b", "strong", "i", "em", "u", "s", "strike", "sub", "sup",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "code",
  "span", "div", "a", "hr", "table", "thead", "tbody", "tr", "th", "td",
]);

// Removed with their content
const DROP = "script,style,iframe,object,embed,form,input,button,textarea,select,link,meta,svg,math,template,img,video,audio";

// Block tags, drawn as a block <span> where only inline content is allowed (e.g. inside an option label)
export const BLOCK_TAGS = new Set([
  "p", "div", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "hr",
  "table", "thead", "tbody", "tr", "th", "td",
]);
export const VOID_TAGS = new Set(["br", "hr"]);

const ENTITIES = { "&lt;": "<", "&gt;": ">", "&amp;": "&", "&quot;": '"', "&#39;": "'", "&nbsp;": " " };
const decodeMath = (math) => math.replace(/&(?:lt|gt|amp|quot|#39|nbsp);/g, (e) => ENTITIES[e]);

const mask = (text) => {
  const math = [];
  const masked = String(text).replace(MATH, (m) => {
    math.push(m);
    return `${math.length - 1}`;
  });
  return { masked, math };
};

const unmask = (text, math) => text.replace(/(\d+)/g, (_, i) => decodeMath(math[Number(i)] ?? ""));

const parse = (masked) => {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${masked}</body>`, "text/html");
  doc.body.querySelectorAll(DROP).forEach((node) => node.remove());
  return doc.body;
};

/** True when the value holds HTML formatting (outside its maths). */
export const isRichHtml = (text) => {
  if (!text) return false;
  const value = String(text);
  if (!value.includes("<") && !value.includes("&")) return false;
  const { masked } = mask(value);
  return TAG.test(masked) || ENTITY.test(masked);
};

/**
 * The value as a tree of safe nodes, or null when it is not HTML:
 *   { type: "text", value }                      text with its LaTeX restored
 *   { type: "element", tag, attrs, children }    an allowed tag (unknown tags keep only their text)
 */
export const parseRichHtml = (text) => {
  if (!isRichHtml(text)) return null;
  const { masked, math } = mask(text);

  const walk = (node) => {
    if (node.nodeType === 3) return [{ type: "text", value: unmask(node.textContent, math) }];
    if (node.nodeType !== 1) return [];

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).flatMap(walk);
    if (!ALLOWED.has(tag)) return children;

    const attrs = {};
    if (tag === "a") {
      const href = (node.getAttribute("href") || "").trim();
      if (/^(https?:|mailto:|\/|#)/i.test(href)) {
        attrs.href = href;
        attrs.target = "_blank";
        attrs.rel = "noopener noreferrer";
      }
    }
    if ((tag === "td" || tag === "th") && /^\d+$/.test(node.getAttribute("colspan") || "")) {
      attrs.colSpan = Number(node.getAttribute("colspan"));
    }
    if ((tag === "td" || tag === "th") && /^\d+$/.test(node.getAttribute("rowspan") || "")) {
      attrs.rowSpan = Number(node.getAttribute("rowspan"));
    }
    if (tag === "ol" && /^\d+$/.test(node.getAttribute("start") || "")) attrs.start = Number(node.getAttribute("start"));

    return [{ type: "element", tag, attrs, children }];
  };

  return Array.from(parse(masked).childNodes).flatMap(walk);
};

/** The value as one line of plain text (tags removed, entities decoded), e.g. for a table cell. */
export const richTextToPlain = (text) => {
  if (!isRichHtml(text)) return text || "";
  const { masked, math } = mask(text);
  const spaced = masked.replace(/<br\s*\/?>|<\/(?:p|div|li|h[1-6]|tr|td|th)>/gi, (m) => `${m} `);
  return unmask(parse(spaced).textContent.replace(/[\s ]+/g, " ").trim(), math);
};
